/**
 * SkillsCategoryTabs — Compact tabbed skills browser by category with internal scroll
 * Includes pinned "Instaladas" tab showing installed skills with uninstall option
 */
import { useState } from 'preact/hooks';
import { useAvailableSkillsWithStatus, type AvailableSkill } from '../hooks/useAvailableSkillsWithStatus';
import { T } from '../styles/tokens';
import { Button } from './ui';

const INSTALLED_TAB = '__installed__';
const FAVORITES_TAB = '__favorites__';
const ALL_TAB = '__all__';

const CATS: Record<string, { emoji: string; color: string; label: string }> = {
 social: { emoji: '', color: T.primary, label: 'Social' },
 'social-media': { emoji: '', color: T.primary, label: 'Social Media' },
 finance: { emoji: '', color: T.success, label: 'Finanças' },
 agents: { emoji: '', color: T.accent, label: 'Agentes' },
 web: { emoji: '', color: '#06b6d4', label: 'Web' },
 'web-scraping': { emoji: '', color: '#06b6d4', label: 'Web Scraping' },
 development: { emoji: '', color: T.primary, label: 'Dev' },
 email: { emoji: '', color: T.purple, label: 'Email' },
 productivity: { emoji: '', color: T.success, label: 'Produtividade' },
 content: { emoji: '', color: T.danger, label: 'Conteúdo' },
 business: { emoji: '', color: T.warning, label: 'Business' },
 automation: { emoji: '', color: T.warning, label: 'Automação' },
 ai: { emoji: '', color: T.purple, label: 'AI' },
 data: { emoji: '', color: T.warning, label: 'Data' },
 communication: { emoji: '', color: T.danger, label: 'Comunicação' },
 cloud: { emoji: '', color: '#06b6d4', label: 'Cloud' },
 devops: { emoji: '', color: '#06b6d4', label: 'DevOps' },
 security: { emoji: '', color: T.danger, label: 'Segurança' },
 utilities: { emoji: '', color: T.textMuted, label: 'Utilitários' },
 general: { emoji: '', color: T.textMuted, label: 'Geral' },
 marketing: { emoji: '', color: T.warning, label: 'Marketing' },
 'seo-marketing': { emoji: '', color: T.warning, label: 'SEO & Marketing' },
 memory: { emoji: '', color: T.purple, label: 'Memória' },
 'smart-home': { emoji: '', color: T.success, label: 'Smart Home' },
 documents: { emoji: '', color: T.textMuted, label: 'Documentos' },
 news: { emoji: '', color: T.warning, label: 'Notícias' },
 gaming: { emoji: '', color: T.danger, label: 'Gaming' },
 ecommerce: { emoji: '', color: T.success, label: 'E-Commerce' },
 design: { emoji: '', color: T.danger, label: 'Design' },
};

function getCat(category: string) {
  return CATS[category?.toLowerCase()] || CATS.general!;
}

interface Props {
  machineId: string;
  isOnline: boolean;
}

export function SkillsCategoryTabs({ machineId, isOnline }: Props) {
  const {
    loading, error, installedCount, availableCount,
    installing, uninstalling, searchQuery, setSearchQuery,
    categories, skills, refetch, installSkill, uninstallSkill,
  } = useAvailableSkillsWithStatus(machineId);

  const [selectedTab, setSelectedTab] = useState<string>(INSTALLED_TAB);
  const [localSearch, setLocalSearch] = useState('');
  const [confirmUninstall, setConfirmUninstall] = useState<string | null>(null);

  const isInstalledTab = selectedTab === INSTALLED_TAB;
  const isFavoritesTab = selectedTab === FAVORITES_TAB;
  const isAllTab = selectedTab === ALL_TAB;
  const activeCategory = isInstalledTab || isFavoritesTab || isAllTab ? null : (selectedTab || categories[0] || 'general');

  // Skills grouped by category
  const skillsByCategory: Record<string, AvailableSkill[]> = {};
  for (const s of skills) {
    const cat = s.category || 'general';
    if (!skillsByCategory[cat]) skillsByCategory[cat] = [];
    skillsByCategory[cat].push(s);
  }

  // Installed skills
  const installedSkills = skills.filter(s => s.installed);

  // Favorite skills
  const favoriteSkills = skills.filter(s => s.isFavorite);
  const favoriteCount = favoriteSkills.length;

  // Current skills based on tab + search
  const baseSkills = isInstalledTab ? installedSkills : isFavoritesTab ? favoriteSkills : isAllTab ? skills : (skillsByCategory[activeCategory!] || []);
  const currentSkills = baseSkills.filter(s => {
    if (!localSearch) return true;
    const q = localSearch.toLowerCase();
    return s.name.toLowerCase().includes(q) ||
      s.description.toLowerCase().includes(q) ||
      (s.tags || []).some(t => t.toLowerCase().includes(q));
  });

  const handleUninstall = async (skillName: string) => {
    const ok = await uninstallSkill(skillName);
    if (ok) setConfirmUninstall(null);
  };

  // Loading
  if (loading) {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{
              width: 80, height: 32, borderRadius: 16,
              background: T.bgCard,
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
          ))}
        </div>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} style={{
            height: 64, borderRadius: 10, marginBottom: 8,
            background: T.bgSubtle, border: '1px solid var(--border)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }} />
        ))}
        <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
      </div>
    );
  }

  // Error
  if (error && skills.length === 0) {
    return (
      <div style={{
        padding: '28px 20px', textAlign: 'center', borderRadius: 12,
        background: T.dangerBg, border: `1px solid ${T.dangerBg}`,
      }}>
 <div style={{ fontSize: 32, marginBottom: 8 }}></div>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Erro ao carregar skills</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>{error}</div>
        <Button variant="ghost" onClick={() => refetch()} style={{
          padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
          background: T.purpleBg, color: T.purple,
          border: `1px solid ${T.purpleBgStrong}`, cursor: 'pointer',
 }}>Tentar Novamente</Button>
      </div>
    );
  }

  // Empty
  if (!loading && availableCount === 0) {
    return (
      <div style={{
        padding: '28px 20px', textAlign: 'center', borderRadius: 12,
        background: T.border, border: '1px solid var(--border)',
      }}>
 <div style={{ fontSize: 32, marginBottom: 8 }}></div>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Nenhum skill disponível</div>
      </div>
    );
  }

  const catInfo = isInstalledTab
    ? { emoji: '', color: T.success, label: 'Instaladas' }
    : isFavoritesTab
 ? { emoji: '', color: T.warning, label: 'Favoritos' }
    : isAllTab
 ? { emoji: '', color: T.purple, label: 'Todos' }
    : getCat(activeCategory!);

  return (
    <div>
      {/* Offline banner */}
      {!isOnline && (
        <div style={{
          padding: '8px 14px', borderRadius: 10, marginBottom: 12, fontSize: 12,
          background: T.warningBg, border: `1px solid ${T.warningBg}`,
          color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: 8,
 }}>Runner offline — mostrando cache local</div>
      )}

      {/* Summary bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 14, gap: 8, flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 13, color: 'var(--text)' }}>
          <strong style={{ color: T.purple }}>{installedCount}</strong> de{' '}
          <strong>{availableCount}</strong> instalados
        </span>
        <Button variant="ghost" onClick={() => refetch()} style={{
          padding: '4px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600,
          border: '1px solid var(--border)', background: T.bgCard,
          color: 'var(--text-muted)', cursor: 'pointer',
 }}>Sincronizar</Button>
      </div>

      {/* Category Tabs — "Instaladas" pinned first */}
      <div style={{
        display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 12,
        scrollbarWidth: 'thin',
        WebkitOverflowScrolling: 'touch',
      }}>
        {/* Pinned "Instaladas" tab */}
        <Button variant="ghost"
          onClick={() => { setSelectedTab(INSTALLED_TAB); setLocalSearch(''); setConfirmUninstall(null); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0,
            transition: 'all 0.15s',
            background: isInstalledTab ? T.successBg : T.successBg,
            color: isInstalledTab ? T.success : '#10B981aa',
            border: isInstalledTab ? `1.5px solid ${T.successBgStrong}` : `1px solid ${T.successBgStrong}`,
          }}
        >
          <span style={{ fontSize: 13 }}></span>
          Instaladas
          <span style={{
            fontSize: 10, fontWeight: 700,
            background: isInstalledTab ? T.successBg : T.successBg,
            padding: '1px 6px', borderRadius: 8, color: T.success,
          }}>{installedCount}</span>
        </Button>

 {/* Pinned "Favoritos" tab */}
        <Button variant="ghost"
          onClick={() => { setSelectedTab(FAVORITES_TAB); setLocalSearch(''); setConfirmUninstall(null); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0,
            transition: 'all 0.15s',
            background: isFavoritesTab ? T.warningBg : T.warningBg,
            color: isFavoritesTab ? T.warning : '#F59E0Baa',
            border: isFavoritesTab ? `1.5px solid ${T.warningBgStrong}` : `1px solid ${T.warningBgStrong}`,
          }}
        >
 <span style={{ fontSize: 13 }}></span>
          Favoritos
          <span style={{
            fontSize: 10, fontWeight: 700,
            background: isFavoritesTab ? T.warningBg : T.warningBg,
            padding: '1px 6px', borderRadius: 8, color: T.warning,
          }}>{favoriteCount}</span>
        </Button>

        {/* "Todos" tab */}
        <Button variant="ghost"
          onClick={() => { setSelectedTab(ALL_TAB); setLocalSearch(''); setConfirmUninstall(null); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0,
            transition: 'all 0.15s',
            background: isAllTab ? T.purpleBg : T.purpleBg,
            color: isAllTab ? T.purple : '#8B5CF6aa',
            border: isAllTab ? `1.5px solid ${T.purpleBgStrong}` : `1px solid ${T.purpleBgStrong}`,
          }}
        >
 <span style={{ fontSize: 13 }}></span>
          Todos
          <span style={{
            fontSize: 10, fontWeight: 700,
            background: isAllTab ? T.purpleBg : T.purpleBg,
            padding: '1px 6px', borderRadius: 8, color: T.purple,
          }}>{availableCount}</span>
        </Button>

        {/* Separator */}
        <div style={{
          width: 1, height: 24, alignSelf: 'center', flexShrink: 0,
          background: 'var(--border)', margin: '0 2px',
        }} />

        {/* Category tabs */}
        {categories.map(cat => {
          const c = getCat(cat);
          const isActive = !isInstalledTab && cat === activeCategory;
          const count = skillsByCategory[cat]?.length || 0;
          return (
            <Button variant="ghost"
              key={cat}
              onClick={() => { setSelectedTab(cat); setLocalSearch(''); setConfirmUninstall(null); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0,
                transition: 'all 0.15s',
                background: isActive ? `${c.color}20` : T.bgCard,
                color: isActive ? c.color : 'var(--text-muted)',
                border: isActive ? `1.5px solid ${c.color}50` : '1px solid var(--border)',
              }}
            >
              <span style={{ fontSize: 13 }}>{c.emoji}</span>
              {c.label}
              <span style={{
                fontSize: 10, fontWeight: 700, opacity: 0.7,
                background: isActive ? `${c.color}15` : T.bgCard,
                padding: '1px 5px', borderRadius: 8,
              }}>{count}</span>
            </Button>
          );
        })}
      </div>

      {/* Search within tab */}
      <div style={{ marginBottom: 10 }}>
        <input
          type="text"
 placeholder={`Buscar em ${catInfo.label}...`}
          value={localSearch}
          onInput={(e: any) => setLocalSearch(e.target.value)}
          style={{
            width: '100%', padding: '8px 14px', borderRadius: 10, boxSizing: 'border-box',
            border: '1px solid var(--border)', background: T.bgCard,
            color: 'var(--text)', fontSize: 12, outline: 'none',
          }}
        />
      </div>

      {/* Results count */}
      <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 8 }}>
        {currentSkills.length} skill{currentSkills.length !== 1 ? 's' : ''} em {catInfo.emoji} {catInfo.label}
        {localSearch && ` — "${localSearch}"`}
      </div>

      {/* Confirm uninstall dialog */}
      {confirmUninstall && (
        <div style={{
          padding: '12px 16px', borderRadius: 10, marginBottom: 10,
          background: T.dangerBg, border: `1px solid ${T.dangerBgStrong}`,
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 12, color: 'var(--text)', flex: 1 }}>
 Desinstalar <strong>{confirmUninstall}</strong>?
          </span>
          <Button variant="ghost"
            onClick={() => handleUninstall(confirmUninstall)}
            disabled={!!uninstalling}
            style={{
              padding: '5px 14px', borderRadius: 8, fontSize: 11, fontWeight: 700,
              background: T.dangerBg, color: T.danger,
              border: `1px solid ${T.dangerBgStrong}`, cursor: uninstalling ? 'not-allowed' : 'pointer',
            }}
 >{uninstalling === confirmUninstall ? '...' : 'Confirmar'}</Button>
          <Button variant="ghost"
            onClick={() => setConfirmUninstall(null)}
            style={{
              padding: '5px 14px', borderRadius: 8, fontSize: 11, fontWeight: 600,
              background: T.bgCard, color: 'var(--text-muted)',
              border: '1px solid var(--border)', cursor: 'pointer',
            }}
          >Cancelar</Button>
        </div>
      )}

      {/* Scrollable skills list */}
      <div style={{
        maxHeight: 400, overflowY: 'auto', borderRadius: 10,
        border: '1px solid var(--border)',
        scrollbarWidth: 'thin',
        scrollbarColor: `${T.purpleBgStrong} transparent`,
      }}>
        {currentSkills.length === 0 ? (
          <div style={{ padding: '28px 20px', textAlign: 'center' }}>
 <div style={{ fontSize: 24, marginBottom: 6 }}>{isInstalledTab ? '' : ''}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {isInstalledTab ? 'Nenhum skill instalado ainda.' : 'Nenhum skill encontrado.'}
            </div>
          </div>
        ) : (
          currentSkills.map((skill, idx) => {
            const cat = getCat(skill.category);
            const isInstalling = installing === skill.name;
            const isUninstalling = uninstalling === skill.name;
            return (
              <div
                key={skill.name}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 14px', minHeight: 60,
                  background: idx % 2 === 0 ? 'transparent' : T.bgSubtle,
                  borderBottom: idx < currentSkills.length - 1 ? '1px solid var(--border)' : 'none',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={(e: any) => e.currentTarget.style.background = T.purpleBg}
                onMouseLeave={(e: any) => e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : T.bgSubtle}
              >
                {/* Emoji */}
                <span style={{ fontSize: 22, flexShrink: 0, width: 32, textAlign: 'center' }}>
                  {skill.emoji || cat.emoji}
                </span>

                {/* Name + desc */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      fontSize: 13, fontWeight: 700, color: 'var(--text)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{skill.name}</span>
                    <span style={{ fontSize: 10, color: 'var(--text-dim)', flexShrink: 0 }}>v{skill.version}</span>
                    {skill.isFavorite && !isFavoritesTab && (
                      <span style={{
                        fontSize: 9, fontWeight: 700, color: T.warning,
                        background: T.warningBg, padding: '1px 5px', borderRadius: 6,
                        flexShrink: 0,
 }}></span>
                    )}
                    {skill.installed && !isInstalledTab && (
                      <span style={{
                        fontSize: 9, fontWeight: 700, color: T.success,
                        background: T.successBg, padding: '1px 5px', borderRadius: 6,
                        flexShrink: 0,
                      }}></span>
                    )}
                  </div>
                  <div style={{
                    fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.3, marginTop: 2,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {skill.description || 'Sem descrição'}
                  </div>
                </div>

                {/* Action button */}
                <div style={{ flexShrink: 0 }}>
                  {skill.installed ? (
                    <Button variant="ghost"
                      onClick={() => setConfirmUninstall(skill.name)}
                      disabled={isUninstalling || !isOnline}
                      style={{
                        padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                        cursor: isUninstalling || !isOnline ? 'not-allowed' : 'pointer',
                        color: confirmUninstall === skill.name ? T.danger : T.success,
                        background: confirmUninstall === skill.name
                          ? T.dangerBg
                          : T.successBg,
                        border: confirmUninstall === skill.name
                          ? `1px solid ${T.dangerBgStrong}`
                          : `1px solid ${T.successBgStrong}`,
                        opacity: !isOnline ? 0.5 : 1,
                        transition: 'all 0.15s',
                        whiteSpace: 'nowrap',
                      }}
                    >
 {isUninstalling ? '...' : '− Desinstalar'}
                    </Button>
                  ) : (
                    <Button variant="ghost"
                      onClick={() => installSkill(skill.name)}
                      disabled={isInstalling || !isOnline}
                      style={{
                        padding: '5px 14px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                        cursor: isInstalling || !isOnline ? 'not-allowed' : 'pointer',
                        color: T.text,
                        background: isInstalling
                          ? T.purpleBg
                          : 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
                        border: `1px solid ${T.purpleBgStrong}`,
                        opacity: !isOnline ? 0.5 : 1,
                        transition: 'all 0.15s',
                        whiteSpace: 'nowrap',
                      }}
                    >
 {isInstalling ? '...' : '+ Instalar'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Custom scrollbar styles */}
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
      `}</style>
    </div>
  );
}
